// Which org a screen shows and what money it may show (TASK-240, REQ-092). The same org screen
// modules serve two surfaces: the AllCode admin (this app, staff, the org from the route
// `/customers/:orgId/…`) and the proxy web app's Admin area (web/home registers this module as the
// area with id "admin" under `/admin`, the customer surface, the org is the session's own). The
// host, not `/v1/me`, says which mount this is: `/v1/me` has no customer audience.
//
// Money: the server nulls what a principal may not see (ADR-054, moneypolicy.For); this is the
// one client rule that keeps a screen from showing a figure the policy hides, mirroring
// CostVisibility.HidesCostFrom. Staff see both figures. An org principal sees list cost only while
// the org's costVisibility is `list_cost`, and price as the server sends it (whether the month is
// billed is the server's, REQ-051). Settings not loaded yet show no money at all (fail closed).
import { createContext } from "preact";
import { useContext } from "preact/hooks";
import type { components } from "./schema";
import { ScopeContext, type ScopeMe } from "../scope/scope";

export type CostVisibility = components["schemas"]["CostVisibility"];
export type Surface = "staff" | "customer";

/** Where this module is mounted; the proxy's Admin area mirrors web/home's `Area` id and path. */
export type OrgHost =
  | { readonly kind: "allcode-admin" }
  | { readonly kind: "proxy-admin-area"; readonly areaId: "admin"; readonly basePath: string };

export const ALLCODE_ADMIN_HOST: OrgHost = { kind: "allcode-admin" };

/** The host web/home's Admin area provides when it mounts these screens (TASK-257). */
export function proxyAdminAreaHost(basePath = "/admin"): OrgHost {
  return { kind: "proxy-admin-area", areaId: "admin", basePath };
}

/** Provided by whatever mounts the screens; the AllCode admin needs no provider. */
export const OrgHostContext = createContext<OrgHost>(ALLCODE_ADMIN_HOST);

export interface MoneyPolicy {
  /** `costMicroUsd`, the AWS list cost. */
  readonly list: boolean;
  /** `priceMicroUsd`, the org's plan price (null from the server for a month not billed). */
  readonly price: boolean;
}

export interface MoneyFields {
  costMicroUsd: number | null;
  priceMicroUsd: number | null;
}

export interface OrgContext {
  readonly orgId: string;
  readonly surface: Surface;
  readonly moneyPolicy: MoneyPolicy;
}

const ALL_MONEY: MoneyPolicy = { list: true, price: true };
const NO_MONEY: MoneyPolicy = { list: false, price: false };

/** AllCode staff: a super-admin or a platform principal (HidesCostFrom never hides from them). */
export function isStaff(me: ScopeMe): boolean {
  return me.role === "super-admin" || me.role === "platform";
}

/**
 * The surface: the proxy's Admin area is always the customer's; in the AllCode admin an org-admin
 * session is the customer surface too (TC-240's fallback run), anyone else is staff.
 */
export function surfaceFor(host: OrgHost, me: ScopeMe): Surface {
  if (host.kind === "proxy-admin-area") return "customer";
  return isStaff(me) ? "staff" : "customer";
}

/** The one money rule; `costVisibility` null means the org's settings are not loaded yet. */
export function moneyPolicyFor(surface: Surface, costVisibility: CostVisibility | null): MoneyPolicy {
  if (surface === "staff") return ALL_MONEY;
  if (costVisibility === null) return NO_MONEY;
  return { list: costVisibility === "list_cost", price: true };
}

/** The figures with what the policy hides set to null; never invents one the server left out. */
export function applyMoneyPolicy<T extends MoneyFields>(policy: MoneyPolicy, fields: T): T {
  return {
    ...fields,
    costMicroUsd: policy.list ? fields.costMicroUsd : null,
    priceMicroUsd: policy.price ? fields.priceMicroUsd : null,
  };
}

export interface OrgContextInputs {
  host: OrgHost;
  me: ScopeMe;
  /** The route's `:orgId`, when the screen has one. */
  routeOrgId?: string | undefined;
  costVisibility: CostVisibility | null;
}

/**
 * The org and what may be shown of it, or null when there is none to show: staff with no org in
 * the route, a customer session without an org, or a route naming another org than the
 * customer's own (an org admin never reaches another org's screens).
 */
export function resolveOrgContext({ host, me, routeOrgId, costVisibility }: OrgContextInputs): OrgContext | null {
  const surface = surfaceFor(host, me);
  const orgId = surface === "staff" ? routeOrgId : me.orgId;
  if (!orgId) return null;
  if (surface === "customer" && routeOrgId !== undefined && routeOrgId !== orgId) return null;
  return { orgId, surface, moneyPolicy: moneyPolicyFor(surface, costVisibility) };
}

/** The screen's org context; null while signed out (no scope) or when `resolveOrgContext` finds no org. */
export function useOrgContext(routeOrgId: string | undefined, costVisibility: CostVisibility | null): OrgContext | null {
  const host = useContext(OrgHostContext);
  const { me } = useContext(ScopeContext);
  if (me === null) return null;
  return resolveOrgContext({ host, me, routeOrgId, costVisibility });
}
